import { Link } from "react-router-dom";
import { ArrowRight, Award, Target, Heart, Users, Linkedin } from "lucide-react";
import { Button } from "@/components/ui/button";

const executives = [
  {
    name: "Sanjeev Bhat",
    role: "Managing Partner",
    image: "https://customer-assets.emergentagent.com/job_tech-solutions-246/artifacts/rlmmqm63_Sanjeev_Bhat_headshot.jpeg",
    bio: "Sanjeev brings over two decades of enterprise technology leadership to Nidus. Before Nidus, he spent 8 years as a Principal at AWS, driving cloud adoption strategies for enterprise customers across multiple verticals. Prior to that, he served as a Senior Director at Vendavo for 10 years, leading large-scale pricing and commercial excellence platform initiatives for Fortune 500 clients. His deep expertise in SaaS platforms, cloud architecture, and digital transformation enables Nidus to deliver solutions that create lasting business impact."
  },
  {
    name: "Chirag Jain",
    role: "Chief Operating Officer",
    image: "https://customer-assets.emergentagent.com/job_tech-solutions-246/artifacts/yn1ek0am_Chirag_Jain_headshot1.png",
    bio: "Chirag has extensive experience operating offshore delivery organizations, building and scaling high-performance technology teams across global locations. His expertise in operational excellence, resource management, and cross-cultural team leadership ensures that Nidus consistently delivers on time and within budget. He oversees day-to-day operations, client delivery, and strategic growth initiatives across the company's India offices."
  },
  {
    name: "Manish Gupta",
    role: "Director of Solution Delivery",
    image: "https://customer-assets.emergentagent.com/job_tech-solutions-246/artifacts/ctpiwez7_Manish_Gupta_headshot.jpeg",
    bio: "Manish is an industry veteran with multiple decades of experience building high-performing delivery teams that consistently exceed client expectations. His hands-on approach to solution architecture, combined with his ability to mentor and develop engineering talent, has made him instrumental in Nidus's reputation for delivery excellence. He leads the technical delivery practice, ensuring that every engagement is executed with precision, quality, and a relentless focus on business outcomes."
  },
  {
    name: "Amir Amirrezvani",
    role: "Chief Customer Officer",
    image: "https://customer-assets.emergentagent.com/job_tech-solutions-246/artifacts/n5pzz1lx_0C00B361-9A35-4A2E-8DC7-738EA47A5CE2.png",
    bio: "Amir Amirrezvani is an accomplished technology and transformation executive with over 20 years of international experience leading digital innovation, cloud strategy and enterprise software consulting. Before joining Nidus Technologies, Amir was a VP and General Manager leading Cloud Operations, Managed Services, Support and IT worldwide at Vendavo for 9 years. Prior to that, Amir was a Director at Cerner for 11 years leading technology and medical device integration teams across Europe and Latin America."
  },
  {
    name: "Mark Anderson",
    role: "Strategic Advisor",
    image: "https://customer-assets.emergentagent.com/job_tech-solutions-246/artifacts/zqs07yh9_IMG_8204.jpeg",
    bio: "Mark Anderson is a seasoned enterprise software executive with more than 30 years of experience leading global services, customer success, and business transformation organizations. His current position is Managing Partner and Founder at RIMS \u2013 Revenue Intelligence Made Simple, and previously held senior leadership positions at industry-leading companies including Model N, Vendavo, SAP, and Comergent, helping global enterprises accelerate growth and maximize the value of their technology investments. As Strategic Advisor to Nidus Technologies, Mark provides guidance on customer success, services excellence, operational scale, and growth strategy. His extensive experience building high-performing organizations and delivering transformational outcomes helps support Nidus Technologies\u2019 continued expansion across logistics, supply chain, enterprise applications, cloud, AI, and digital transformation services."
  }
];

const values = [
  {
    icon: Award,
    title: "Excellence",
    description: "We measure our success by the success of our clients. We strive for excellence in every project we undertake."
  },
  {
    icon: Target,
    title: "Innovation",
    description: "We stay at the forefront of industry trends and emerging technologies to provide cutting-edge solutions."
  },
  {
    icon: Heart,
    title: "Integrity",
    description: "We believe in transparent communication, honest advice, and doing what's right for our clients."
  },
  {
    icon: Users,
    title: "Collaboration",
    description: "We work as true partners with our clients, understanding their unique challenges and goals."
  }
];

const partners = [
  "Artesient",
  "Allwyn Corporation", 
  "Dupladamas",
  "Palo Alto Consulting",
  "Brains Technology Solutions"
];

export default function About() {
  return (
    <div data-testid="about-page" className="page-transition">
      {/* Hero Section */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
                About Nidus Technologies
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none text-slate-900 font-['Outfit'] mb-6">
                Your Trusted Technology Partner
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed font-['Plus_Jakarta_Sans']">
                At Nidus, we empower businesses with innovative technology solutions to thrive 
                in the digital age. Our passion for technology, combined with our commitment to 
                excellence, enables us to deliver tailored solutions that drive success.
              </p>
            </div>
            <div className="aspect-[4/3] overflow-hidden border border-slate-200">
              <img 
                src="https://images.pexels.com/photos/1313534/pexels-photo-1313534.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Nidus Office"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="bg-slate-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-4">
              Our Mission
            </p>
            <blockquote className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white font-['Outfit'] tracking-tight leading-snug">
              "To empower organizations with innovative technology solutions that drive 
              sustainable growth and competitive advantage."
            </blockquote>
          </div>
        </div>
      </section>

      {/* Leadership Section */}
      <section data-testid="leadership-section" className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
              Our Leadership
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-['Outfit']">
              Meet the Team
            </h2>
            <p className="text-slate-600 mt-4 font-['Plus_Jakarta_Sans']">
              Led by seasoned technology executives with decades of combined experience across enterprise software, cloud platforms, and global delivery.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {executives.slice(0, 3).map((exec) => (
              <div key={exec.name} data-testid={`executive-card-${exec.name.toLowerCase().replace(/\s/g, '-')}`} className="group">
                <div className="aspect-[3/4] overflow-hidden border border-slate-200 mb-6">
                  <img 
                    src={exec.image}
                    alt={exec.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h3 className="text-xl font-bold text-slate-900 font-['Outfit']">
                  {exec.name}
                </h3>
                <p className="text-blue-600 font-medium text-sm mb-4 font-['Plus_Jakarta_Sans']">
                  {exec.role}
                </p>
                <p className="text-sm text-slate-600 leading-relaxed font-['Plus_Jakarta_Sans']">
                  {exec.bio}
                </p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mt-10">
            {executives.slice(3).map((exec) => (
              <div key={exec.name} data-testid={`executive-card-${exec.name.toLowerCase().replace(/\s/g, '-')}`} className="group">
                <div className="aspect-[3/4] overflow-hidden border border-slate-200 mb-6">
                  <img 
                    src={exec.image}
                    alt={exec.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h3 className="text-xl font-bold text-slate-900 font-['Outfit']">
                  {exec.name}
                </h3>
                <p className="text-blue-600 font-medium text-sm mb-4 font-['Plus_Jakarta_Sans']">
                  {exec.role}
                </p>
                <p className="text-sm text-slate-600 leading-relaxed font-['Plus_Jakarta_Sans']">
                  {exec.bio}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
              What Drives Us
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-['Outfit']">
              Our Core Values
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value) => (
              <div key={value.title} className="bg-white p-8 border border-slate-200">
                <div className="w-12 h-12 bg-blue-100 flex items-center justify-center mb-6">
                  <value.icon className="w-6 h-6 text-blue-700" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 font-['Outfit'] mb-3">
                  {value.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed font-['Plus_Jakarta_Sans']">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-700 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] mb-4">
            Want to Join Our Team?
          </h2>
          <p className="text-blue-100 mb-8 font-['Plus_Jakarta_Sans']">
            We're always looking for talented individuals who share our passion for technology.
          </p>
          <Link to="/careers">
            <Button 
              data-testid="about-careers-button"
              className="bg-white text-blue-700 hover:bg-slate-100 rounded-none px-8"
            >
              View Open Positions
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
